import { Route, Routes, useLocation } from 'react-router';
import OrganizationLayout from '@/components/layouts/OrganizationLayout';
import { OrganizationMembershipRole } from '@/gql/enums';
import AIAssistantPage from '@/pages/organizationAdmin/AssistantPage';
import BillingPage from '@/pages/organizationAdmin/Billing';
import DashboardPage from '@/pages/organizationAdmin/Dashboard';
import DownloadLeadsPage from '@/pages/organizationAdmin/DownloadLeadsPage';
import LeadDetailsPage from '@/pages/organizationAdmin/LeadDetails';
import LeadsPage from '@/pages/organizationAdmin/LeadsPage';
import NotificationsPage from '@/pages/organizationAdmin/Notifications';
import UploadReportPage from '@/pages/organizationAdmin/UploadReport';
import WorkloadPage from '@/pages/organizationAdmin/Workload';
import NotFound from '../components/blocks/NotFound';
import CenteredLayout from '../components/layouts/CenteredLayout';
import { useUser } from '../contexts/UserSession';
import InvitationPage from '../pages/Invitation';
import LoginPage from '../pages/LoginPage';
import MSALCallback from '../pages/LoginPage/MSAL/MSALCallback';
import OIDCCallback from '../pages/LoginPage/OIDC/OIDCCallback';
import McpAuthorizePage from '../pages/McpAuthorizePage';
import OrganizationPage from '../pages/Organization';
import ProfilePage from '../pages/ProfilePage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import SignOutPage from '../pages/SignOutPage';
import Users from '../pages/Users';
import RedirectTo from '../RedirectTo';
import runtime from '../runtime';

const OrganizationRoutes = () => {
    const user = useUser();
    const location = useLocation();

    if (!runtime.organization) {
        return <NotFound homePath={null} />;
    }

    if (!user) {
        return (
            <Routes>
                <Route element={<CenteredLayout />} path="/invitation/*">
                    <Route element={<InvitationPage />} index />
                    <Route element={<NotFound homePath="/invitation/" />} path="*" />
                </Route>
                <Route element={<CenteredLayout />} path="/login/*">
                    <Route element={<LoginPage />} index />
                    <Route element={<ResetPasswordPage />} path="resetPassword" />
                    <Route element={<MSALCallback />} path="sso/msal" />
                    <Route element={<OIDCCallback />} path="sso/oidc" />
                    <Route element={<NotFound homePath="/login/" />} path="*" />
                </Route>
                <Route
                    element={
                        <RedirectTo
                            state={{ from: location.pathname + location.search, fromState: location.state }}
                            to="/login/"
                        />
                    }
                    path="*"
                />
            </Routes>
        );
    }

    const redirectTo = (
        <RedirectTo to={location.state?.from || '/'} state={{ ...location.state?.fromState, isAuthenticated: true }} />
    );

    return (
        <Routes>
            <Route element={redirectTo} path="/login/*" />
            <Route element={redirectTo} path="/invitation/*" />
            {/* MCP OAuth authorization page - requires authentication but uses centered layout */}
            <Route element={<CenteredLayout />} path="/authorize/mcp/:requestId">
                <Route element={<McpAuthorizePage />} index />
            </Route>
            <Route element={<OrganizationLayout />} path="*">
                <Route
                    element={
                        <RedirectTo
                            to={
                                user.organizationRole === OrganizationMembershipRole.Member ? '/workload' : '/dashboard'
                            }
                        />
                    }
                    index
                />
                {user.organizationRole !== OrganizationMembershipRole.Member && (
                    <Route element={<DashboardPage />} path="dashboard" />
                )}
                <Route element={<LeadsPage />} path="leads" />
                <Route element={<LeadDetailsPage />} path="leads/:id" />
                <Route element={<DownloadLeadsPage />} path="downloadLeads" />
                <Route element={<WorkloadPage />} path="workload" />
                <Route element={<AIAssistantPage />} path="assistant" />
                <Route element={<NotificationsPage />} path="notifications" />
                <Route element={<UploadReportPage />} path="uploads/:id" />
                <Route element={<BillingPage />} path="billing" />
                <Route element={<OrganizationPage />} path="organization" />
                <Route element={<Users />} path="users" />
                <Route element={<ProfilePage />} path="profile" />
                <Route element={<SignOutPage />} path="signout" />
                <Route element={<NotFound homePath="/" />} path="*" />
            </Route>
        </Routes>
    );
};

export default OrganizationRoutes;
